import angular from 'angular';
import { IConfiguracaoDocumentoForm } from '../models/configuracaodocumento.model';
import { ITipoArquivoResponse } from '../../../TipoArquivos/models/tipo-arquivos.service.model';
import { ConfiguracaoDocumentosModalService } from '../modal/configuracaodocumentos.modal.service';
import { TipoArquivosService } from '../../../TipoArquivos/tipo-arquivos.service';
import { applyNsjTableInlineMobileLabels } from '../../../../utils/nsj-table';
import { ModalConfirmService } from '../../../../shared/components/modal-confirm/modal.confirm.service';
import { TiposRelatoriosSolicitacoesService } from '../../../TiposRelatoriosSolicitacoes/tipos-relatorios-solicitacoes.service';
import { ITipoRelatorioSolicitacaoResponse } from '../../../TiposRelatoriosSolicitacoes/models/tipos-relatorios-solicitacoes.service.model';

/** Tipos de solicitação cujos relatórios compõem o kit de documentos admissionais. */
const TIPOS_SOLICITACAO_ADMISSAO = [12, 15];

/** Tipo de documento que recebe o kit admissional no GED. */
const CODIGO_DOCUMENTOS_ADMISSIONAIS = 'DOCUMENTOS_ADMISSIONAIS';

type ConfiguracaoDocumentosField = {
    value: string;
    label: string;
    type?: string;
    style?: string;
    copy?: string;
    customCollumnHtml?: () => string;
    customCollumnScope?: Record<string, (entity: ITipoArquivoResponse) => any>;
};

export class ConfiguracaoDocumentosIndexController {
    static $inject = [
        'ConfiguracaoDocumentosModalService',
        'TipoArquivosService',
        'ModalConfirmService',
        'NewToaster',
        'TiposRelatoriosSolicitacoesService',
        '$sce',
    ];

    public documentTypes: ITipoArquivoResponse[] = [];
    public displayedDocumentTypes: ITipoArquivoResponse[] = [];
    public fields: ConfiguracaoDocumentosField[] = [];
    public documentosAdmissionais: ITipoRelatorioSolicitacaoResponse[] = [];
    public activeTab = 1;
    public ajudaAssinaturaAdmissionais: any;
    public exibeAssinaturaDocumentosAdmissionais = false;
    private documentosAdmissionaisCarregados = false;

    constructor(
        private configuracaoDocumentosModalService: ConfiguracaoDocumentosModalService,
        private tipoArquivosService: TipoArquivosService,
        private modalConfirmService: ModalConfirmService,
        private NewToaster: any,
        public tiposRelatoriosSolicitacoesService: TiposRelatoriosSolicitacoesService,
        private $sce: angular.ISCEService,
    ) {
        this.ajudaAssinaturaAdmissionais = this.$sce.trustAsHtml(
            '<p>O documento vai para assinatura se estiver marcado aqui ou se o modelo já tiver campo de assinatura.</p>' +
            '<p>Para o colaborador assinar, o tipo "Documentos admissionais" também precisa estar com ' +
            '<b>Requer assinatura</b>.</p>' +
            '<p>Vale para os próximos documentos liberados. Os já entregues precisam ser liberados de novo.</p>'
        );
        this.initializeFields();
        this.initializeEntities();
    }

    /**
     * Carrega os documentos do kit admissional configuráveis pelo cliente.
     *
     * Registro global (tenant nulo) é modelo padrão da Nasajon: a assinatura dele é definida pela tag
     * no próprio PDF e o banco nem permite alterá-lo por tenant, então fica fora da lista.
     */
    public initializeDocumentosAdmissionais(): void {
        // carrega na primeira abertura da aba: recarregar a cada clique traria requisicao repetida e
        // poderia sobrescrever uma marcacao que o usuario acabou de fazer
        if (this.documentosAdmissionaisCarregados) {
            return;
        }

        this.documentosAdmissionais = [];

        this.tiposRelatoriosSolicitacoesService
            .getList()
            .then((tiposRelatorios) => {
                this.documentosAdmissionais = (tiposRelatorios || []).filter(
                    (tipoRelatorio) =>
                        !!tipoRelatorio.tenant &&
                        TIPOS_SOLICITACAO_ADMISSAO.indexOf(tipoRelatorio.tiposolicitacao) !== -1
                );
                this.documentosAdmissionaisCarregados = true;
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao carregar os documentos admissionais',
                    body: error?.data?.message || 'Ocorreu um erro ao carregar os documentos admissionais.'
                });
            });
    }

    /** Grava o "requer assinatura" do documento; em caso de erro desfaz a marcação na tela. */
    public alterarAssinaturaDocumentoAdmissional(entity: ITipoRelatorioSolicitacaoResponse): void {
        const valorAnterior = !entity.requerassinatura;

        this.tiposRelatoriosSolicitacoesService
            .updateRequerassinatura(entity.tiporelatorio, entity.requerassinatura)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: entity.requerassinatura
                        ? 'Documento passará a exigir assinatura do colaborador.'
                        : 'Documento não exigirá mais assinatura do colaborador.'
                });
            })
            .catch((error) => {
                entity.requerassinatura = valorAnterior;
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao salvar a configuração de assinatura',
                    body: error?.data?.message || 'Ocorreu um erro ao salvar a configuração de assinatura.'
                });
            });
    }

    public initializeEntities(): void {
        this.clearEntities();
        this.tipoArquivosService
            .getList()
            .then((documentTypes) => {
                this.syncEntities(documentTypes);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao carregar tipos de documentos',
                    body: error?.data?.message || 'Ocorreu um erro ao carregar os tipos de documentos.'
                });
            });
    }

    public initializeFields(): void {
        this.fields = applyNsjTableInlineMobileLabels([
            {
                value: 'titulo',
                label: 'Tipo de documento',
                type: 'string',
                style: 'title',
                copy: '',
            },
            {
                value: 'tipopadrao',
                label: 'Tipo padrão',
                type: 'string',
                style: 'title',
                copy: '',
                customCollumnHtml: () => `
                    <span
                        class="document-config-status"
                        ng-class="{'document-config-status--required': entity.tipopadrao}"
                    >
                        {{entity.tipopadrao ? 'Sim' : 'Não'}}
                    </span>
                `,
            },
            {
                value: 'exigeliberacao',
                label: 'Requer liberação',
                type: 'string',
                style: 'title',
                copy: '',
                customCollumnHtml: () => `
                    <span
                        class="document-config-status"
                        ng-class="{'document-config-status--required': entity.exigeliberacao}"
                    >
                        {{entity.exigeliberacao ? 'Sim' : 'Não'}}
                    </span>
                `,
            },
            {
                value: 'assinatura',
                label: 'Requer assinatura',
                type: 'string',
                style: 'title',
                copy: '',
                customCollumnHtml: () => `
                    <span
                        class="document-config-status"
                        ng-class="{'document-config-status--required': entity.assinatura}"
                    >
                        {{entity.assinatura ? 'Sim' : 'Não'}}
                    </span>
                `,
            },
            {
                value: 'tipoarquivo',
                label: 'Ações',
                type: 'string',
                style: 'title',
                copy: '',
                customCollumnScope: {
                    editarTipoDocumento: (entity: ITipoArquivoResponse) => this.editarTipoDocumento(entity),
                    excluirTipoDocumento: (entity: ITipoArquivoResponse) => this.excluirTipoDocumento(entity),
                    podeExcluirTipoDocumento: (entity: ITipoArquivoResponse) => this.podeExcluirTipoDocumento(entity),
                    tooltipExcluirTipoDocumento: (entity: ITipoArquivoResponse) => this.tooltipExcluirTipoDocumento(entity),
                },
                customCollumnHtml: () => `
                    <div>
                        <button
                            type="button"
                            class="btn btn-default btn-sm"
                            ng-click="customCollumnScope.editarTipoDocumento(entity)"
                            uib-tooltip="Editar"
                            aria-label="Editar tipo de documento"
                        >
                            <i class="fas fa-edit"></i>
                        </button>
                        <span
                            uib-tooltip="{{customCollumnScope.tooltipExcluirTipoDocumento(entity)}}"
                            aria-label="{{customCollumnScope.tooltipExcluirTipoDocumento(entity)}}"
                            style="display: inline-block;"
                        >
                            <button
                                type="button"
                                class="btn btn-default btn-sm"
                                ng-click="customCollumnScope.excluirTipoDocumento(entity)"
                                ng-disabled="!customCollumnScope.podeExcluirTipoDocumento(entity)"
                                aria-label="Excluir tipo de documento"
                            >
                                <i class="fas fa-trash"></i>
                            </button>
                        </span>
                    </div>
                `,
            }
        ]);
    }

    public criarTipoDocumento(): void {
        const modal = this.configuracaoDocumentosModalService.open({
            mode: 'create',
            documento: {
                codigo: '',
                titulo: '',
                descricao: '',
                exigeliberacao: true,
                assinatura: false,
                prazoassinaturapadrao: null,
                tipopadrao: false,
            },
        });

        modal.result
            .then(() => {
                this.initializeEntities();
            })
            .catch(() => undefined);
    }

    public editarTipoDocumento(entity: ITipoArquivoResponse): void {
        const modal = this.configuracaoDocumentosModalService.open({
            mode: 'edit',
            documento: this.mapResponseToForm(entity),
        });

        modal.result
            .then(() => {
                this.initializeEntities();
            })
            .catch(() => undefined);
    }

    public podeExcluirTipoDocumento(entity: ITipoArquivoResponse): boolean {
        return !entity?.tipopadrao;
    }

    public tooltipExcluirTipoDocumento(entity: ITipoArquivoResponse): string {
        if (!this.podeExcluirTipoDocumento(entity)) {
            return 'Tipo de documento padrão não pode ser excluído.';
        }

        return 'Excluir';
    }

    public excluirTipoDocumento(entity: ITipoArquivoResponse): void {
        if (!this.podeExcluirTipoDocumento(entity)) {
            return;
        }

        const modal = this.modalConfirmService.open(
            entity,
            'tipo de documento',
            true,
            this.buildDeleteConfirmationText(entity),
            true,
        );

        modal.result
            .then(() => {
                this.executarExclusao(entity);
            })
            .catch(() => undefined);
    }

    public totalDocumentTypes(): number {
        return this.documentTypes.length;
    }

    public displayedDocumentTypesCount(): number {
        return this.displayedDocumentTypes.length;
    }

    private executarExclusao(entity: ITipoArquivoResponse): void {
        const identifier = entity.tipoarquivo;
        if (!identifier) {
            return;
        }

        this.tipoArquivosService
            .delete(identifier)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tipo de documento excluído com sucesso!'
                });

                this.initializeEntities();
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao excluir tipo de documento',
                    body: error?.data?.message || 'Ocorreu um erro ao tentar excluir o tipo de documento.'
                });
            });
    }

    private buildDeleteConfirmationText(entity: ITipoArquivoResponse): Record<string, string> {
        let subtitle = entity.codigo && entity.titulo ?
            `Você está prestes a excluir ${entity.codigo} - ${entity.titulo}.` : '';

        return {
            title: 'Excluir tipo de documento',
            subtitle,
            confirmText: 'Tem certeza que deseja excluir este tipo de documento?',
            closeButton: 'Cancelar',
            confirmButton: 'Sim, excluir',
        };
    }

    private mapResponseToForm(entity: ITipoArquivoResponse): IConfiguracaoDocumentoForm {
        return {
            tipoarquivo: entity.tipoarquivo,
            codigo: entity.codigo,
            titulo: entity.titulo,
            descricao: entity.descricao || '',
            assinatura: entity.assinatura,
            exigeliberacao: entity.exigeliberacao,
            prazoassinaturapadrao: entity.prazoassinaturapadrao,
            tipopadrao: entity.tipopadrao,
        };
    }

    private syncEntities(documentTypes: ITipoArquivoResponse[] = []): void {
        const items = angular.copy(documentTypes);
        this.documentTypes = items;
        this.displayedDocumentTypes = [...items];
        this.atualizarExibicaoAssinaturaDocumentosAdmissionais();
    }

    /**
     * A aba de assinatura só faz sentido com o tipo "Documentos admissionais" exigindo assinatura: é ele que
     * libera a ação de assinar no Meu Trabalho. Calculado quando a lista de tipos muda, e não num getter
     * avaliado a cada digest. Fica fora do clearEntities para a aba não piscar a cada recarga da lista.
     */
    private atualizarExibicaoAssinaturaDocumentosAdmissionais(): void {
        const tipoAdmissional = this.documentTypes.find(
            (documentType) => documentType.codigo === CODIGO_DOCUMENTOS_ADMISSIONAIS
        );

        this.exibeAssinaturaDocumentosAdmissionais = !!tipoAdmissional && !!tipoAdmissional.assinatura;

        // a aba sumiu enquanto estava aberta (tipo desmarcado): volta para a listagem
        if (!this.exibeAssinaturaDocumentosAdmissionais && this.activeTab === 2) {
            this.activeTab = 1;
        }
    }

    private clearEntities(): void {
        this.documentTypes = [];
        this.displayedDocumentTypes = [];
    }
}
